<template>
  <div class="page-header-container">
    <h3 class="invitation-string">{{getInvitationString}}</h3>
    <h1 id="presTitleHeading" class="page-heading gutter-bottom-40">{{ title }}</h1>

    <div v-if="startDate" class="gutter-bottom-40">
      <h2 id="startDate">{{ getRegFlowDate }}</h2>
      <h2 id="startTime">{{ getRegFlowTime }}</h2>
      <h2 id="duration">{{ getDuration }}</h2>
    </div>

    <div v-if="topic">
      <div class="page-sub-title gutter-bottom-10">
        <div ref="topic">
          {{ updateSeeMoreBasedOnTopicContent() }}
          <br/>
          <span class="see-more" v-if="characterCount < topic.length">
            <span class="cursor" @click="toggleModalWindow(true)">{{ $t("login.see_more") }}</span>
          </span>
        </div>
      </div>
      <Modal :show="showModalWindow" :onClose="toggleModalWindow">
        <div class="container">
          <h1 id="presTitleHeadingModal" class="page-heading gutter-bottom-40">{{ title }}</h1>
          <div v-if="startDate" class="gutter-bottom-40">
            <h2 id="startDateInModal">{{ startDate }}</h2>
          </div>

          <pre class="pre-wrap" v-if="topic">{{ topic }}</pre>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/components/common/Modal';
import { fitStringContentToHeightIfPossible } from "@/utils/fitTextUtility";
import Constants from '@/utils/constants';
import { useCorporateBranding } from '@/composables';

// Define component name
defineOptions({
  name: 'page-header'
});

// Props
const props = defineProps({
  title: { type: String, required: true },
  startDate: { type: String, required: true },
  duration: { type: String, required: true },
  topic: { type: String, required: true }
});

// Composables
const { t } = useI18n();
const { brandingData } = useCorporateBranding();

// Reactive data
const characterCount = ref(150);
const showSeeMoreBtn = ref(false);
const showModalWindow = ref(false);

// Methods
const updateSeeMoreBasedOnTopicContent = () => {
  const width = document.body.clientWidth;
  
  if (width <= 1024) {
    characterCount.value = 90;
  }
  else if (width <= 1500) {
    characterCount.value = 120;
  }

  const ellipsisSymbol = (characterCount.value < props.topic.length) ? '...' : ''
  return `${props.topic.slice(0, characterCount.value)}${ellipsisSymbol}`;
};

const toggleModalWindow = (show) => {
  showModalWindow.value = show;
};

const adjustHeadingTextToOneLineIfPossible = () => {
  const headingElement = document.querySelector('#presTitleHeading');
  fitStringContentToHeightIfPossible(headingElement, 32, 32, 22);
};

// Computed properties
const getInvitationString = computed(() => {
  let invString = t(Constants.Translation.LoginInvitationString);
  if (brandingData.value && brandingData.value.CustomMessages) {
    const index = brandingData.value.CustomMessages.findIndex((val) => {
      return val.Key === 'InvitationString';
    });
    if (index !== -1 && brandingData.value.CustomMessages[index].Value.length !== 0) {
      invString = brandingData.value.CustomMessages[index].Value;
    }
  }
  return invString;
});

const getRegFlowTime = computed(() => {
  const match = (/\s\d\d\d\d\s/.exec(props.startDate)).index + 5;
  const time = props.startDate.substring(match);
  return `${time}`;
});

const getRegFlowDate = computed(() => {
  const dayIndex = props.startDate.split(',', 1).join(',').length;
  const day = props.startDate.substring(0, dayIndex);
  const match = (/\s\d\d\d\d\s/.exec(props.startDate)).index + 5;
  const index = props.startDate.split(',', 1).join(',').length;
  const date = props.startDate.substring(index + 1, match);
  return `${day}, ${date}`;
});

const getDuration = computed(() => {
  return `${props.duration} min`;
});

// Lifecycle hooks
onMounted(() => {
  adjustHeadingTextToOneLineIfPossible();
});
</script>

<style scoped lang="stylus">
.container 
  height 100%;

.gutter-bottom-40
  margin-bottom: 40px;
.gutter-bottom-10
  margin-bottom: 10px;

.pre-wrap
  white-space pre-wrap;
  line-height 1.4;
  height calc(100% - 125px);
  overflow hidden;
  overflow-y auto;
  word-wrap break-word;
   
.cursor
  cursor: pointer;
  text-decoration: underline;
.modal-window
  & :deep(.modal-content)
    height: calc(100% - 12%);
    box-shadow: 0px 0px 7px 1px rgba(0, 0, 0, 0.1);
    border: none;
.see-more {
  text-align: center;
  color: #285298;
  font-size: 15px;
  padding: 5px 5px 5px 0;
}
.page-sub-title {
  color: #43425D;
  font-size: 0.9375rem;
  line-height: 20px;
  min-height: 60px;
  overflow: hidden;
  position: relative;
  padding: 0 5px;
}
.page-header-container {
  text-align: center;
  margin: 50px auto 20px auto;
  color: #43425D; 
  @media screen and (max-width: 1480px) {
    margin: 82px 30% 0 0;
  }
  @media screen and (max-width: 1280px) {
    margin: 32px 30% 0 0;
  }
  margin: 137px 30% 0 0;
  text-align: left;
  font-family: 'Lato', sans-serif;
  #startDate {
    color: #3a4145;
    font-weight: 400;
    font-size: 18px
    padding: 10px 0 4px 0;
    @media screen and (max-width: 768px) {
      font-size: 14px;
    }
  }
  #startTime
  #duration {
    color: #3a4145;
    font-weight: 400;
    font-size: 18px;
    padding: 4px 0;
    @media screen and (max-width: 768px) {
      font-size: 14px;
    }
  }
}
.invitation-string {
  padding-bottom: 15px;
  color: #3a4145;
  font-weight: 400;
  font-size: 14px;
  @media screen and (max-width: 768px) {
    font-size: 13px;
  }
}
.page-heading {
  font-size: 26px;
}
#presTitleHeading {
  color: #3a4145;
  max-width: 448px;
  font-weight: 700;
  font-size: 32px;
  margin-bottom: 20px;
  @media screen and (max-width: 768px) {
    font-size: 22px;
    width: 320px;
  }
}
</style>
